import React, { useContext, useEffect, useState } from "react";
import "./layout.css";
import { Outlet, useLocation, useParams, useNavigate } from "react-router-dom";

import { AppContext } from "../../context/AppContext";

import TopNav from "../../components/TopNav";
import { Loader, Profile, TabVisibility } from "../../components";
import { encryptData, decryptData, getFromStorage, setToStorage } from "../../assets";
import { makeGetAPICall, makePostAPICall } from "../../helpers/ApiHelpers";
import Swal from "sweetalert2";

const Layout = () => {
	let navigate = useNavigate();
	let params = useParams();
	let location = useLocation();

	const { setExam, exam, user } = useContext(AppContext);
	const [loading, setLoading] = useState(false);
	const count = TabVisibility();

	const markExam = (answers, examQuestions) => {
		setLoading(true);
		let questions = [];
		for (const property in answers) {
			const answer = answers[property];
			const questionType = answer?.startsWith("option") ? "objective" : "theory";
			const finalAnswer = answer?.startsWith("option") ? answers[property].substr(6, 1) : answer;

			questions.push({
				id: property,
				type: questionType,
				answer: finalAnswer,
			});
		}

		let duration = decryptData(getFromStorage("timeLeft"));
let tabSwitchCount = decryptData(getFromStorage("tsc")) || 0;
		makePostAPICall(
			"exams.php",
			{
				action: "submit_exam",
				user_id: user.id,
				exam_id: exam?.id,
				marks: exam?.marks_per_answer,
				duration,
							submission_type: "tab_switch_limit",
				tab_switch_count: tabSwitchCount,
				questions,
			},
			(response) => {
				setToStorage("result", JSON.stringify(response.data));
				navigate(`/exam/${params.id}/end`);
				setExam((prevState) => {
					const { questions, ...others } = prevState;
					return { ...others, examStatus: "ended" };
				});
				setLoading(false);
			},
			() => markExam(answers, examQuestions)
		);
	};

	function shuffleArray(array) {
		return array.sort(() => Math.random() - 0.5);
	}

	useEffect(() => {
		//? ─── STEP 1: No exam in context yet ───────────────────────────────────────
		//? exam is null — either this is a fresh load, or the context was cleared.
		//? Fetch the exam details by the id from the URL, store in context, then
		//? return early. Without the early return, execution falls through to STEP 4
		//? where exam is still null, hits the else, calls setLoading(false), and the
		//? page renders blank before the async response even comes back.
		if (!exam) {
			setLoading(true);
			makeGetAPICall(
				"exams.php",
				{ action: "list_exam_by_id", exam_id: params.id },
				(response) => {
					//? Exam fetched — push into context. This triggers a re-render
					//? and the useEffect fires again, this time with exam populated,
					//? so execution moves into STEP 2 and below on the next cycle.
					setExam(response.data[0]);
					setLoading(false);
				},
				() => {
					//? Fetch failed — notify the student and stop loading.
					Swal.fire({
						icon: "error",
						title: "Oops...",
						text: "Couldn't get this exam. Please inform a teacher, refresh and try again",
					});
					setLoading(false);
				}
			);

			//? Return early — do not fall through to question/start_exam logic below.
			//? exam is null here so none of the subsequent steps would work anyway.
			return;
		}

		//? ─── STEP 2: Exam in context but it belongs to a different exam ───────────
		//? This guards against stale context — e.g. a student finished exam/5 and
		//? the context still holds that data when they navigate to exam/28.
		//? Both ids are cast to String() before comparing because exam.id comes from
		//? the API response (can be integer or string depending on PHP's json_encode),
		//? while params.id from React Router is always a string. Without the cast,
		//? strict !== would treat 28 !== "28" as true and fire navigate() on every
		//? single render — creating a redirect loop that prevents STEP 4 from ever
		//? being reached (which is why get_exam_questions and start_exam never fired).
		if (String(exam?.id) !== String(params.id)) {
			//? Clear the stale exam from context so STEP 1 re-fetches the correct one.
			//? Without this, navigate() re-renders the component but exam still holds
			//? the wrong data, STEP 2 fires again, and you get an infinite redirect loop.
			setExam(null);
			return navigate(`/exam/${params.id}`);
		}

		//? ─── STEP 3: Exam already ended ───────────────────────────────────────────
		//? examStatus is set to "ended" inside markExam after submit_exam completes.
		//? If we get here with a finished exam, stop loading and exit — we don't want
		//? to re-fetch questions or re-fire start_exam for an exam that's already done.
		if (exam?.examStatus === "ended") {
			setLoading(false);
			return;
		}

		//? ─── STEP 4: Correct exam loaded, not ended, questions not fetched yet ─────
		//? This fires on the second useEffect cycle — after STEP 1 fetched and stored
		//? the exam. exam.questions doesn't exist yet so both API calls are fired now:
		//? 	- get_exam_questions: fetches and stores the question list in context
		//? 	- start_exam: logs the start on the server (for timing and audit trails)
		//? Both are fired in parallel intentionally — start_exam returns no useful data
		//? so there's no benefit in waiting for it before fetching questions.
		//? Also catches the case where exam was rehydrated from localStorage with stale
		//? questions from a previous session — questions is cleared in that case by
		//? STEP 2's setExam(null), so this block will correctly re-fetch them.
		if (!exam?.questions) {
			setLoading(true);

			//? Fetch the question list for this exam
			makeGetAPICall(
				"questions.php",
				{ action: "get_exam_questions", exam_id: params.id },
				(response) => {
					let questions = response.data;

					//? Split into objective and theory arrays so we can apply shuffle
					//? logic only to objective questions (which have selectable options).
					//? Theory questions have free-text answers — no options to shuffle.
					let objectiveQuestions = questions?.filter((question) => question?.type === "objective");
					let theoryQuestions = questions?.filter((question) => question?.type === "theory");

					//? If shuffle_options is enabled, randomise the order of each objective
					//? question's option keys. This prevents students from memorising answer
					//? positions across multiple attempts on the same exam.
					//? Either way, attach optionsArray to every objective question so the
					//? render logic is consistent regardless of whether shuffle is on or off.
					if (exam?.shuffle_options === "1") {
						objectiveQuestions = objectiveQuestions?.map((question) => {
							let optionsArray = shuffleArray(Object.keys(question?.options));
							return { optionsArray, ...question };
						});
					} else {
						objectiveQuestions = objectiveQuestions.map((question) => {
							let optionsArray = Object.keys(question.options);
							return { optionsArray, ...question };
						});
					}

					//? Recombine into a single array — objective questions first, then theory.
					//? ?? [] ensures we get an empty array rather than undefined if either
					//? filter returns no results (e.g. an exam with no theory questions).
					questions = [...(objectiveQuestions ?? []), ...(theoryQuestions ?? [])];

					//? Merge questions into the existing exam context using the functional form
					//? of setExam so we don't overwrite the exam details already stored there.
					setExam((prevState) => ({ ...prevState, questions }));
					setLoading(false);
				},
				() => {
					//? Question fetch failed — notify student and stop loading so they
					//? aren't stuck on the loader with no way to recover.
					Swal.fire({
						icon: "error",
						title: "Oops...",
						text: "Couldn't get questions for this exam. Please inform a teacher, refresh and try again",
					});
					setLoading(false);
				}
			);

			//? Log the exam start on the server, fired in parallel with get_exam_questions.
			//? This is a fire-and-forget call — we use the response for nothing, it's
			//? purely a server-side record used for duration tracking, attempt logging,
			//? and malpractice detection (submission_type, tab_switch_count etc).
			makePostAPICall(
				"exams.php",
				{
					action: "start_exam",
					user_id: user.id,
					exam_id: exam?.id,
				},
				() => { },
				() => {
					//? start_exam failed — warn the student but don't block the exam.
					//? The student can still proceed; the failure is surfaced via the alert.
					Swal.fire({
						icon: "error",
						title: "Oops...",
						text: "Couldn't start your exam. Please inform a teacher, refresh and try again",
					});
				}
			);

			//? Return early — loading is managed inside the async callbacks above.
			return;
		}

		//? ─── STEP 5: Exam loaded, questions already in context ────────────────────
		//? All data is ready — exam details and questions are both in context.
		//? Nothing left to fetch or fire. Just stop the loader so the UI renders.
		setLoading(false);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [exam]);

	//? Persist exam state to encrypted storage on every change so it survives a
	//? page refresh. encryptData prevents the exam data from being readable in
	//? plain text from the browser's dev tools or localStorage inspector.
	useEffect(() => {
		setToStorage("exam", encryptData(exam));
	}, [exam]);

	//? ─── Tab switch / malpractice detection ───────────────────────────────────────
	//? count is the cumulative tab-switch count from the TabVisibility hook, and
	//? increments every time the student leaves and returns to this tab.
	//? 	- If switch_tab_limit is 0 or not set, skip the entire block.
	//? 	- If count === 0 the hook just initialised — skip to avoid a false-positive
	//? 	  warning firing on the very first render before the student has done anything.
	//? 	- If count hits the configured limit, force-submit the exam via markExam
	//? 	  and warn the student they have exceeded the allowed number of switches.
	//? 	- Otherwise warn the student they switched but haven't hit the limit yet.
	useEffect(() => {
		if (parseInt(exam?.switch_tab_limit || 0) > 0) {
			if (count === 0) return;
			if (count === parseInt(exam?.switch_tab_limit)) {
				Swal.fire({
					icon: "warning",
					title: "Warning",
					text: "You have switched tabs too many times",
				});
				const answers = decryptData(getFromStorage("answers"));
				const examQuestions = exam?.questions;
				markExam(answers, examQuestions);
			} else {
				Swal.fire({
					icon: "warning",
					title: "Warning",
					text: "You have switched tab",
				});
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [count]);

	//? Show the full-screen loader while any async operation is in progress.
	if (loading) return <Loader />;

	//? The exam start and review pages use a slimmer layout — just TopNav + content.
	//? No Profile sidebar or layout wrapper needed on these pages.
	if (location.pathname.endsWith("start") || location.pathname.endsWith("review"))
		return (
			<div>
				<TopNav />
				<Outlet />
			</div>
		);

	//? Default layout — includes the Profile sidebar alongside the main content area.
	return (
		<div>
			<TopNav />
			<div className="my-container">
				<div className="layouts-container">
					<Profile />
					<div className="layout-tb-container">
						<Outlet />
					</div>
				</div>
			</div>
		</div>
	);
};

export default Layout;